import React, { useState, useEffect } from "react";
import { API_URL } from "../../config";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import AntDesignTable from "../common/antTable/AntDesignTable";
import { Button, Col, Popconfirm, Row, Select, message } from "antd";
import CommonModal from "../common/commonModal/CommonModal";
import MedicineForm from "./MedicineForm";
import { Form } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";

const MedicineContainer = () => {
  const { user } = useAuth();
  const [medicineData, setMedicineData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [medicineTypes, setMedicineTypes] = useState([]);
  const [medicineNames, setMedicineNames] = useState([]);
  const [dosageRoutes, setDosageRoutes] = useState([]);
  const [medicineModal, setMedicineModal] = useState(false);
  const [form] = Form.useForm();

  const [selectedName, setSelectedName] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/medicine/?skip=0&limit=100`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      })
      .then((response) => {
        setMedicineData(response.data);
        setFilteredData(response.data);
        setMedicineTypes([
          ...new Set(response.data.map((item) => item.medicine_type)),
        ]);
        setMedicineNames(response.data.map((item) => item.name));
        setDosageRoutes([
          ...new Set(response.data.map((item) => item.dosage_route)),
        ]);
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
      });
  }, []);

  useEffect(() => {
    filterData();
  }, [selectedName, selectedType, selectedRoute]);

  const handleSelectChange = (value, label) => {
    if (label === "Medication Name") {
      setSelectedName(value);
    } else if (label === "Medication Type") {
      setSelectedType(value);
    } else if (label === "Dosage Route") {
      setSelectedRoute(value);
    }
  };

  const filterData = () => {
    let filtered = medicineData;

    if (selectedName) {
      filtered = filtered.filter((item) => item.name === selectedName);
    }
    if (selectedType) {
      filtered = filtered.filter((item) => item.medicine_type === selectedType);
    }
    if (selectedRoute) {
      filtered = filtered.filter((item) => item.dosage_route === selectedRoute);
    }

    setFilteredData(filtered);
  };

  const medicineModalOpen = () => {
    setMedicineModal(true);
  };
  const medicineModalClose = () => {
    setMedicineModal(false);
    setIsEditMode(false);
    setCurrentRecord(null);
    form.resetFields();
  };

  const handleConfirm = () => {
    form.validateFields().then((values) => {
      const formattedValues = {
        name: values.medicine,
        manufacturer: values.manufacturer,
        medicine_type: values.medicine_type,
        dosage_route: values.dosage_route,
        unit_of_measurement: values.unit_of_measurement,
        stock_quantity: values.stock_quantity,
        threshold: values.threshold,
        status: values.status,
      };

      const apiCall = isEditMode
        ? axios.patch(
            `${API_URL}/medicine/${currentRecord.id}`,
            formattedValues,
            {
              headers: { Authorization: `Bearer ${user.token}` },
            },
          )
        : axios.post(`${API_URL}/medicine/`, formattedValues, {
            headers: { Authorization: `Bearer ${user.token}` },
          });

      apiCall
        .then((response) => {
          const newData = response.data;
          setMedicineData((prevData) =>
            isEditMode
              ? prevData.map((item) =>
                  item.id === newData.id ? newData : item,
                )
              : [newData, ...prevData],
          );
          setFilteredData((prevData) =>
            isEditMode
              ? prevData.map((item) =>
                  item.id === newData.id ? newData : item,
                )
              : [newData, ...prevData],
          );
          setMedicineModal(false);
          form.resetFields();
          setIsEditMode(false);
          setCurrentRecord(null);
        })
        .catch((error) => {
          console.error(": ", error);
          const errorMessage =
            error.response?.data?.detail || "Failed to submit form";
          message.error(errorMessage);
        });
    });
  };

  const handleEditMedicine = (record) => {
    setCurrentRecord(record);
    setIsEditMode(true);
    setMedicineModal(true);
    form.setFieldsValue({
      manufacturer: record.manufacturer,
      medicine: record.name,
      medicine_type: record.medicine_type,
      dosage_route: record.dosage_route,
      unit_of_measurement: record.unit_of_measurement,
      stock_quantity: record.stock_quantity,
      threshold: record.threshold,
      status: record.locked ? "active" : "inactive",
    });
  };

  const handleDeleteMedicine = (id) => {
    axios
      .delete(`${API_URL}/medicine/${id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      })
      .then((response) => {
        setMedicineData((prevData) =>
          prevData.filter((item) => item.id !== id),
        );
        setFilteredData((prevData) =>
          prevData.filter((item) => item.id !== id),
        );
      })
      .catch((error) => {
        console.error("Failed to delete record: ", error);
      });
  };

  const deleteMultipleRecords = () => {
    axios
      .post(`${API_URL}/medicine/delete-request/`, selectedRowKeys, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      })
      .then((response) => {
        setMedicineData((prevData) =>
          prevData.filter((item) => !selectedRowKeys.includes(item.id)),
        );
        setFilteredData((prevData) =>
          prevData.filter((item) => !selectedRowKeys.includes(item.id)),
        );
        setSelectedRowKeys([]);
      })
      .catch((error) => {
        console.error("Failed to delete records: ", error);
      });
  };

  const columns = [
    {
      title: "Medicine Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "medicine_type",
      key: "medicine_type",
    },
    {
      title: "Unit of Measurement",
      dataIndex: "unit_of_measurement",
      key: "unit_of_measurement",
    },
    {
      title: "Inventory",
      dataIndex: "stock_quantity",
      key: "stock_quantity",
    },
    {
      title: "Status",
      dataIndex: "locked",
      key: "locked",
      render: (locked) => (
        <span style={{ color: locked ? "green" : "red" }}>
          {locked ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <span>
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Button
                className="edit-button"
                icon={<EditOutlined />}
                onClick={() => handleEditMedicine(record)}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Popconfirm
                title="Are you sure you want to delete this Medicine?"
                onConfirm={() => handleDeleteMedicine(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  className="delete-button"
                  icon={<DeleteOutlined />}
                  style={{ marginLeft: 8 }}
                />
              </Popconfirm>
            </Col>
          </Row>
        </span>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
        <Button
          style={{ width: "10%" }}
          type="primary"
          icon={<PlusOutlined />}
          onClick={medicineModalOpen}
        >
          Create
        </Button>
        <Button
          style={{ width: "10%" }}
          type="primary"
          danger
          onClick={deleteMultipleRecords}
          disabled={selectedRowKeys.length === 0}
        >
          Delete
        </Button>
      </div>
      <Row gutter={16} style={{ marginBottom: "16px" }}>
        <Col span={6}>
          <Select
            showSearch
            placeholder="Medication Name"
            options={medicineNames.map((name) => ({ value: name }))}
            onChange={(value) => handleSelectChange(value, "Medication Name")}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Select
            showSearch
            placeholder="Medication Type"
            options={medicineTypes.map((type) => ({ value: type }))}
            onChange={(value) => handleSelectChange(value, "Medication Type")}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Select
            showSearch
            placeholder="Dosage Route"
            options={dosageRoutes.map((route) => ({ value: route }))}
            onChange={(value) => handleSelectChange(value, "Dosage Route")}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>
      <AntDesignTable
        columns={columns}
        tableData={filteredData}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      {medicineModal && (
        <CommonModal
          title={isEditMode ? "Edit Medicine" : "Create Medicine"}
          visible={medicineModal}
          onCancel={medicineModalClose}
          onConfirm={handleConfirm}
          confirmButtonText={isEditMode ? "Update" : "Save"}
        >
          <MedicineForm form={form} />
        </CommonModal>
      )}
    </React.Fragment>
  );
};

export default MedicineContainer;
